// src/components/knowledge-base/ArticleGrid.jsx
import React, { useEffect, useId } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  IconButton,
  Button,
  Skeleton,
} from "@mui/material";
import { ReactComponent as Star } from "../../../../assets/images/star.svg";
import { ReactComponent as Like } from "../../../../assets/images/like.svg";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { loadGoogleFont, useScopedFontStyle } from "../../../../utils";




const ArticleGrid = ({
  articles = [],
  title,
  businessName,
  showSeeMore = true,
  marginTop = "100px",
  loading = false,
  skeletonCount = 3,
}) => {
  const { data: kbSettings, initialized } = useSelector((state) => state.externalKbSettings);
  const accentColor = kbSettings?.data?.accent_color;
  const fontFamily = kbSettings?.data?.font_family || "Inter";

  const rawId = useId();
  const scopeId = rawId.replace(/:/g, "");

  // Load Google Font dynamically
  useEffect(() => {
    loadGoogleFont(fontFamily);
  }, [fontFamily]);

  // Inject scoped style — beats global index.scss, zero side effects
  useScopedFontStyle(scopeId, fontFamily);

  const navigate = useNavigate();

  // Loading skeleton
  if (loading || !initialized) {
    return (
      <Box
        data-kb-font-id={scopeId}
        sx={{ width: "100%", marginTop: { xs: "22px", sm: "32px", lg: marginTop } }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: { xs: "20px", sm: "20px", lg: "33px" },
          }}
        >
          <Skeleton variant="text" width="35%" height={50} />
          {showSeeMore && (
            <Skeleton variant="rectangular" width={180} height={52} sx={{ borderRadius: "8px" }} />
          )}
        </Box>

        <Grid container spacing={5}>
          {Array.from({ length: skeletonCount }).map((_, index) => (
            <Grid
              item
              xs={12}
              sm={6}
              md={4}
              key={index}
              sx={{
                flexBasis: { xs: "100%", sm: "50%", md: "33.333333%" },
                maxWidth: { xs: "100%", sm: "50%", md: "33.333333%" },
              }}
            >
              <Box
                sx={{
                  height: "100%",
                  backgroundColor: "#FBFCFC",
                  padding: { xs: "20px", lg: "30px" },
                  borderRadius: "8px",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Skeleton variant="circular" width={48} height={48} sx={{ mb: 2 }} />
                <Box sx={{ flexGrow: 1 }}>
                  <Skeleton variant="text" height={32} sx={{ mb: 1.2 }} />
                  <Skeleton variant="text" sx={{ mb: 0.8 }} />
                  <Skeleton variant="text" sx={{ mb: 0.8 }} />
                  <Skeleton variant="text" width="70%" sx={{ mb: 5 }} />
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: "auto" }}>
                    <Skeleton variant="text" width="45%" />
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      <Skeleton variant="circular" width={16} height={16} sx={{ mr: 0.5 }} />
                      <Skeleton variant="text" width={50} />
                    </Box>
                  </Box>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  if (articles.length === 0) return null;

  const displayedArticles = showSeeMore ? articles.slice(0, 6) : articles;
  const actualShowSeeMore = showSeeMore && articles.length > 6;

  return (
    <Box
      data-kb-font-id={scopeId}
      sx={{ width: "100%", marginTop: { xs: "22px", sm: "32px", lg: marginTop } }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: { xs: "20px", sm: "20px", lg: "33px" },
        }}
      >
        <Typography
          sx={{
            color: "#0C141B",
            fontSize: { xs: "28px", sm: "36px", lg: "44px" },
            fontStyle: "normal",
            fontWeight: 700,
            lineHeight: "normal",
          }}
        >
          {title}
        </Typography>
        {actualShowSeeMore && (
          <Button
            variant="outlined"
            color="primary"
            sx={{
              borderRadius: "8px",
              width: { xs: "140px", sm: "170px", lg: "200px" },
              textTransform: "none",
              height: { xs: "50px", lg: "60px" },
              border: "1px solid #D5DCE1",
              background: "#FFF",
              color: "#405261",
              fontSize: { xs: "14px", lg: "16px" },
              fontStyle: "normal",
              fontWeight: 600,
              lineHeight: "normal",
              boxShadow: "0 1px 2px 0 rgba(6, 21, 34, 0.05)",
              "&:not(.Mui-disabled):hover": {
                backgroundColor: "#FFFFFF",
                borderColor: "#C5CED6",
              },
              "&.Mui-disabled": {
                color: "#93A1B0",
                border: "1px solid #C5CED6",
                bgcolor: "#F9FAFA",
              },
            }}
          >
            See More
          </Button>
        )}
      </Box>

      <Grid container spacing={{ xs: 2, sm: 2, lg: 5 }}>
        {displayedArticles.map((article, index) => (
          <Grid
            item
            xs={12}
            sm={6}
            md={6}
            lg={4}
            key={index}
            sx={{
              backgroundColor: "white",
              WebkitTapHighlightColor: "transparent", // 🔥 remove mobile blue
            }}
            onClick={(e) => {
              e.stopPropagation();
              const isSubdomain = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1' && window.location.hostname.split('.').length >= 3;
              if (isSubdomain) {
                navigate(`/${article.id}`);
              } else {
                navigate(`/kb/view/${article.id}`, { state: { businessName } });
              }
            }}
          >
            <Card
              sx={{
                cursor: "pointer",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                boxShadow: "none",
                backgroundColor: "#FBFCFC",
                padding: { xs: "20px", sm: "20px", md: "20px", lg: "30px" },

                // 🔥 REMOVE BLUE CLICK / FOCUS EFFECT
                outline: "none",
                WebkitTapHighlightColor: "transparent",

                "&:focus": {
                  outline: "none",
                },
                "&:focus-visible": {
                  outline: "none",
                },
                "&:active": {
                  backgroundColor: "#FBFCFC",
                },
              }}
            >
              <Box>
                <IconButton
                  size="large"
                  disableRipple
                  disableFocusRipple
                  disableTouchRipple
                  sx={{
                    color: accentColor || "#148DFD",
                    backgroundColor: "transparent",
                    "&:hover": { backgroundColor: "transparent" },
                    "&:focus": { outline: "none" },
                  }}
                >
                  <Star fontSize="large" />
                </IconButton>
              </Box>

              <CardContent
                sx={{
                  flexGrow: 1,
                  padding: 0,
                  "&:last-child": { paddingBottom: 0 },
                }}
              >
                <Typography
                  gutterBottom
                  sx={{
                    color: "#0C141B",
                    fontSize: { xs: "20px", lg: "24px" },
                    fontStyle: "normal",
                    fontWeight: 700,
                    lineHeight: { xs: "28px", lg: "34px" },
                    display: "block",
                    whiteSpace: "nowrap",
                    textOverflow: "ellipsis",
                    overflow: "hidden",
                  }}
                >
                  {article.title}
                </Typography>

                <Typography
                  sx={{
                    color: "#405261",
                    fontSize: "14px",
                    fontStyle: "normal",
                    fontWeight: 400,
                    lineHeight: "24px",
                  }}
                >
                  {article.description}
                </Typography>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mt: "40px",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      color: accentColor || "#405261",
                      fontSize: "14px",
                      fontStyle: "normal",
                      fontWeight: 600,
                      lineHeight: "24px",
                      textTransform: "uppercase",
                    }}
                  >
                    {article.views || 0} VIEWS
                  </Typography>

                  <Box sx={{ display: "flex", alignItems: "center", gap: '6px' }}>
                    <Like
                      sx={{
                        fontSize: "small",
                        width: "15px",
                        height: "15px",
                        color: "#405261",
                      }}
                    />
                    <Typography

                      sx={{
                        color: "#405261",
                        fontFamily: "Inter",
                        fontSize: "14px",
                        fontStyle: "normal",
                        fontWeight: 600,
                        lineHeight: "24px", /* 171.429% */
                        textTransform: "uppercase"
                      }}
                    >
                      {article.score || 0}%
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default ArticleGrid;